import React, { useCallback, useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { pickerMessages as m } from './pickerMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type PickerChip = 'all' | 'music' | 'grow' | 'links' | 'pictures' | 'about';

const CHIPS: readonly { value: PickerChip; label: MessageDescriptor }[] = [
  { value: 'all', label: m.chipAll },
  { value: 'music', label: m.groupMusic },
  { value: 'grow', label: m.groupGrow },
  { value: 'links', label: m.chipLinks },
  { value: 'pictures', label: m.chipPictures },
  { value: 'about', label: m.groupAbout },
];

interface ChipProps {
  value: PickerChip;
  label: string;
  isOn: boolean;
  onChange: (value: PickerChip) => void;
}

const ChipComponent: React.FC<ChipProps> = ({ value, label, isOn, onChange }) => {
  const pick = useCallback(() => onChange(value), [onChange, value]);
  return (
    <button type="button" className={clsx(styles.chip, isOn && styles.chipOn)} onClick={pick} aria-pressed={isOn}>
      {label}
    </button>
  );
};

const Chip = TypedMemo(ChipComponent);

interface SMLPickerChipsProps {
  value: PickerChip;
  onChange: (value: PickerChip) => void;
}

/** The filter chips of the picker (design b1): All, Music, Grow your fans, Links, Pictures and text, About you. */
const SMLPickerChipsComponent: React.FC<SMLPickerChipsProps> = ({ value, onChange }) => {
  const intl = useIntl();
  const chips = useMemo(() => CHIPS.map((chip) => ({ ...chip, text: intl.formatMessage(chip.label) })), [intl]);
  return (
    <div className={styles.chips}>
      {chips.map((chip) => (
        <Chip key={chip.value} value={chip.value} label={chip.text} isOn={chip.value === value} onChange={onChange} />
      ))}
    </div>
  );
};

export const SMLPickerChips = TypedMemo(SMLPickerChipsComponent);
